// Core
import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { FieldSearchSelect, RemoteTable } from "components/AtomsV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useGetBilateralPricesByCommunityIdPaginationQuery } from "state/bilateralPrices/bilateralPricesApiSlice";

// Utils
import { useGetMembers } from "utils/crud/read";
import { datetimeToDate, datetimeToTime } from "utils/time";

const price = (v) => (
  <span className="tabular-nums" style={{ color: "var(--accent)", fontWeight: 600 }}>
    {v != null ? Number(v).toFixed(4) : "—"}
  </span>
);

function ListBilateralPrices({ setIsLoading }) {
  const community = useSelector(selectCurrentCommunity);
  const members = useGetMembers("true");
  const { t } = useTranslation(["community"]);

  const [page, setPage] = useState(0);
  const [limit] = useState(10);
  const [sortOrder, setSortOrder] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [receiverUserId, setReceiverUserId] = useState(null);
  const [providerUserId, setProviderUserId] = useState(null);

  // READ Bilateral Prices (server-side pagination / filtering / sorting)
  const { data, isFetching, isLoading } = useGetBilateralPricesByCommunityIdPaginationQuery(
    {
      page,
      limit,
      date,
      time,
      receiverUserId,
      providerUserId,
      communityId: community.id,
      sortColumn: sortOrder?.column,
      sortDirection: sortOrder?.direction,
    },
    {
      pollingInterval: 15000,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    setIsLoading(isLoading || isFetching);
  }, [isLoading, isFetching]);

  const userIdToName = (userId) => members?.find((member) => member.id === userId)?.name;

  const rows = useMemo(
    () =>
      (data?.data ?? []).map((row, i) => ({
        id: `${row.datetime_timestamp}-${row.provider_user_id}-${row.receiver_user_id}-${i}`,
        date: datetimeToDate(row.datetime_timestamp),
        time: datetimeToTime(row.datetime_timestamp),
        provider: row.provider_user_id ? userIdToName(row.provider_user_id) : "—",
        receiver: row.receiver_user_id ? userIdToName(row.receiver_user_id) : "—",
        price: row.price,
      })),
    [data, members]
  );

  const columns = [
    { key: "date", label: t("bilateralPrices.table.columns.date"), sortable: true, width: "15%" },
    { key: "time", label: t("bilateralPrices.table.columns.time"), sortable: true, width: "15%" },
    { key: "provider", label: t("bilateralPrices.table.columns.provider"), width: "22%" },
    { key: "receiver", label: t("bilateralPrices.table.columns.receiver"), width: "22%" },
    {
      key: "price",
      label: t("bilateralPrices.table.columns.price"),
      align: "right",
      render: price,
    },
  ];

  const allLabel = t("filters.all", { defaultValue: "Todos", ns: "common" });

  const dateOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_dates ?? []).map((o) => ({ value: o.date, label: o.date })),
  ];
  const timeOptions = [
    { value: "", label: allLabel },
    ...(data?.distinct_times ?? []).map((o) => ({ value: o.time, label: o.time })),
  ];
  const memberOptions = [
    { value: "", label: allLabel },
    ...(members ?? []).map((m) => ({ value: m.id, label: m.name })),
  ];

  const onFilter = (setter) => (value) => {
    setPage(0);
    setter(value || null);
  };

  const filters = (
    <>
      <FieldSearchSelect
        label={t("bilateralPrices.table.columns.date")}
        value={date ?? ""}
        onChange={onFilter(setDate)}
        options={dateOptions}
      />
      <FieldSearchSelect
        label={t("bilateralPrices.table.columns.time")}
        value={time ?? ""}
        onChange={onFilter(setTime)}
        options={timeOptions}
      />
      <FieldSearchSelect
        label={t("bilateralPrices.table.columns.provider")}
        value={providerUserId ?? ""}
        onChange={onFilter(setProviderUserId)}
        options={memberOptions}
      />
      <FieldSearchSelect
        label={t("bilateralPrices.table.columns.receiver")}
        value={receiverUserId ?? ""}
        onChange={onFilter(setReceiverUserId)}
        options={memberOptions}
      />
    </>
  );

  return (
    <div className="rec-card">
      <RemoteTable
        columns={columns}
        rows={rows}
        totalCount={data?.total_count ?? 0}
        page={page}
        limit={limit}
        onPageChange={setPage}
        sort={sortOrder}
        onSortChange={setSortOrder}
        filters={filters}
        loading={isFetching}
        emptyText={t("bilateralPrices.charts.no_data")}
      />
    </div>
  );
}

ListBilateralPrices.defaultProps = {
  setIsLoading: () => {},
};

ListBilateralPrices.propTypes = {
  setIsLoading: PropTypes.func,
};

export default ListBilateralPrices;
